/**
 * @prettier
 */

// React Packages
import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withTranslation, useTranslation, Trans } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";

// Actions
import { getUUID } from "../../state/actions/configActions";
import { getCompanyName, isInIframe } from "../../services/shared/helpers";
import { submissionStarted } from "../../state/actions/submissionStateActions";

// Components
import CustomButton from "../../components/CustomButton";
import Header from "../../components/Header";
import StartIconAnimated from "../../assets/svg/startScreenAnimatedIcon.svg";

// Images
import logo from "../../assets/img/idpal_logo.svg";

// Alt text
import { imageAlt } from "../../config/accessabilityRules";

// Config
import { ACTION_LABELS } from "../../config/dataDogActionLabels";

const ACTION = ACTION_LABELS.reCaptcha;

const LinkText = ({ linkTo = "#", title = "", ...props }) => {
    return (
        <a
            href={linkTo}
            target="_blank"
            title={title}
            aria-label={{ title }}
            rel="noreferrer"
        >
            {props.children}
        </a>
    );
};

const ReCaptchaPageV1 = ({ getUUID, i18n, branding, submission, policies }) => {
    const recaptchaRef = useRef(null);
    const primaryFocusRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState({
        isRecaptchaEmpty: false,
    });
    const [showIdPoweredByIDPal, setShowIdPoweredByIDPal] = useState(false);
    const [showCustomClientsLogo, setShowCustomClientsLogo] = useState(false);

    const { t } = useTranslation();
    const customClientsLogo = branding?.logo || null;

    const setRecaptchaError = () => {
        recaptchaRef.current.reset();
        setError({
            isRecaptchaEmpty: true,
        });
    };
    const handleReCaptchaSubmit = event => {
        event.preventDefault();
        const recaptchaToken = recaptchaRef.current.getValue();
        if (recaptchaToken.length) {
            setIsSubmitting(true);
            getUUID(recaptchaToken);
        } else {
            setRecaptchaError();
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (primaryFocusRef && primaryFocusRef.current) {
            primaryFocusRef.current.focus();
        }
        if (branding?.whitelabel === 0) {
            setShowIdPoweredByIDPal(true);
        }
        // Show custom client's logo if app is not opened in iframe
        if (customClientsLogo && !isInIframe()) {
            setShowCustomClientsLogo(true);
        }
        document.title = t("idpal_doc_title_start");
        return () => null;
    }, [branding?.whitelabel, customClientsLogo, t]);

    const renderPoweredByIDPal = () => (
        <div className={"identity-mark-section"}>
            <p className="identity-mark-text">{t("idpal_powered_by")}</p>
            <img className="small-logo" src={logo} alt="ID-Pal logo" />
        </div>
    );

    return (
        <div className="root-layout-container start-page">
            <Header showLanguageSelect isLogoHidden={showCustomClientsLogo} />
            <section className="content-wrapper">
                <div className="start-logo-container">
                    {showCustomClientsLogo ? (
                        <img
                            alt={imageAlt.startLogo}
                            className="custom-logo-image"
                            src={`data:image/png;base64,${customClientsLogo}`}
                        />
                    ) : (
                        <div className="icon-group-wrapper">
                            <object
                                className="svg-animation-container"
                                data={StartIconAnimated}
                                aria-label={imageAlt.startLogo}
                            />
                        </div>
                    )}
                </div>
                <div className="start-content-container">
                    <div className="start-content">
                        <h1
                            ref={primaryFocusRef}
                            tabIndex={-1}
                            className="start-text-container"
                        >
                            <Trans
                                i18nKey="idpal_start_screen_default"
                                values={{
                                    companyName: getCompanyName(branding.name),
                                }}
                                components={{
                                    br: <br />,
                                }}
                            />
                        </h1>

                        <form onSubmit={handleReCaptchaSubmit}>
                            <div className="g-recaptcha-wrapper">
                                <div className="g-recaptcha-content">
                                    <div
                                        key={i18n.language}
                                        className={`${
                                            error.isRecaptchaEmpty
                                                ? "g-recaptcha-error-border"
                                                : ""
                                        }`}
                                    >
                                        <ReCAPTCHA
                                            sitekey={
                                                process.env.REACT_APP_SITE_KEY
                                            }
                                            ref={recaptchaRef}
                                            hl={i18n.language}
                                        />
                                    </div>
                                    {error.isRecaptchaEmpty && (
                                        <p className="g-recaptcha-error">
                                            {t("idpal_recaptcha_prompt")}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <p className="start-privacy-policy-text">
                                <Trans
                                    i18nKey="idpal_terms_and_conditions_warning"
                                    values={{
                                        sessionState: submission.new
                                            ? t("idpal_start").toLowerCase()
                                            : t("idpal_continue").toLowerCase(),
                                    }}
                                    components={{
                                        link1: (
                                            <LinkText
                                                linkTo={
                                                    policies.terms_and_conditions
                                                }
                                                title={"Terms and Conditions"}
                                            />
                                        ),
                                        link2: (
                                            <LinkText
                                                linkTo={policies.privacy_policy}
                                                title={"here"}
                                            />
                                        ),
                                    }}
                                />
                            </p>
                            <div className="start-cta-container">
                                <CustomButton
                                    className="btn"
                                    label={t("idpal_start")}
                                    isDisabled={isSubmitting}
                                    handleClick={handleReCaptchaSubmit}
                                    actionDataLabel={ACTION.continueButton}
                                />
                                {showIdPoweredByIDPal && renderPoweredByIDPal()}
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        branding: state.config.profile.data.company_branding,
        policies: state.config.profile.data.policies,
        submission: state.submissionState.details,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({ getUUID, submissionStarted }, dispatch);
}

export default withTranslation("translation")(
    connect(mapStateToProps, mapDispatchToProps)(ReCaptchaPageV1)
);
